import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileUpload } from '@/components/ui/file-upload';
import { formatAmountSummary, formatDate } from '@/utils/formatters';
import { apiFetch } from '@/utils/api';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface Invoice {
    id: string;
    vendor?: { name: string };
    total_amount?: number;
    status: string;
    created_at: string;
}

interface Message {
    type: 'success' | 'error';
    text: string;
}

export default function InvoiceUploadPage() {
    const navigate = useNavigate();
    const params = useParams({ strict: false });
    const routerState = useRouterState();
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
    const [message, setMessage] = useState<Message | null>(null);

    useEffect(() => {
        fetchRecentInvoices();
    }, []);

    const fetchRecentInvoices = async () => {
        try {
            const response = await apiFetch('/api/invoices/?limit=5');
            if (response.ok) {
                const data = await response.json();
                setRecentInvoices(data);
            }
        } catch (error) {
            console.error('Error fetching recent invoices:', error);
        }
    };

    const handleFileSelect = (selectedFile: File | null) => {
        setFile(selectedFile);
        setMessage(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) {
            setMessage({ type: 'error', text: 'Please select a file' });
            return;
        }

        setUploading(true);
        setMessage(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await apiFetch('/api/invoices/upload', {
                method: 'POST',
                body: formData,
                headers: {}, // Don't set Content-Type for FormData, let browser set it
            });

            if (response.ok) {
                const result = await response.json();
                setMessage({
                    type: 'success',
                    text: result.message || 'Invoice uploaded successfully!'
                });
                setFile(null);
                fetchRecentInvoices();
            } else {
                // Try to parse error response
                let errorMessage = 'Upload failed';
                try {
                    const errorResult = await response.json();
                    errorMessage = errorResult.detail || errorResult.message || errorMessage;
                } catch (parseError) {
                    // If we can't parse the error response, use status text
                    errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
                }
                setMessage({ type: 'error', text: errorMessage });
            }
        } catch (error: any) {
            console.error('Upload error:', error);
            setMessage({
                type: 'error',
                text: `Network error: ${error.message || 'Please check your connection and try again'}`
            });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Upload Invoice</h1>
                    <p className="text-muted-foreground">
                        Upload PDF or image files to process invoices automatically
                    </p>
                </div>

                {/* Upload Form */}
                <Card>
                    <CardContent className="space-y-4">
                        <form onSubmit={handleSubmit} className="space-y-2">
                            <FileUpload
                                onFileSelect={handleFileSelect}
                                file={file}
                                disabled={uploading}
                                accept=".pdf,.jpg,.jpeg,.png"
                                maxSize={10 * 1024 * 1024} // 10MB
                            />

                            <Button
                                type="submit"
                                disabled={uploading || !file}
                                className="w-full"
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    'Upload Invoice'
                                )}
                            </Button>
                        </form>

                        {/* Message */}
                        {message && (
                            <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
                                {message.type === 'success' ? (
                                    <CheckCircle className="h-4 w-4" />
                                ) : (
                                    <AlertCircle className="h-4 w-4" />
                                )}
                                <AlertDescription>{message.text}</AlertDescription>
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Invoices */}
                {recentInvoices.length > 0 && (
                    <div>
                        <h1>Recent Invoices</h1>

                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Vendor</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {recentInvoices.map((invoice) => (
                                    <TableRow key={invoice.id}>
                                        <TableCell>
                                            <div className="font-medium text-zinc-950 dark:text-white">
                                                {invoice.vendor?.name || 'Unknown Vendor'}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm text-zinc-500 dark:text-zinc-400">
                                                {invoice.total_amount ? formatAmountSummary(invoice.total_amount) : 'Processing...'}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={invoice.status === 'verified' ? 'default' : 'secondary'}>
                                                {invoice.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm text-zinc-500 dark:text-zinc-400">
                                                {formatDate(invoice.created_at)}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {invoice.status !== 'verified' && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => navigate({ to: `/invoices/${invoice.id}`})}
                                                >
                                                    Correct invoice
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <div className="mt-6 pt-4 border-t">
                            <Button variant="ghost" onClick={() => navigate({ to: '/invoices'})}>
                                View All Invoices →
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/invoices/upload')({
  component: InvoiceUploadPage,
})
